import { useMemo, useState } from "react";
import { Search, Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BLOCK_META } from "@/components/fluxos/blockMeta";
import { useFluxos, type Fluxo } from "@/hooks/useFluxos";
import { MobileSheet } from "./MobileSheet";

interface MobileFluxosSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (fluxo: Fluxo) => void;
}

/**
 * Lista em componente separado: assim o useFluxos só busca os fluxos quando a
 * folha abre, e não em toda conversa que o usuário abre no celular.
 */
const FluxosList = ({ onSelect }: { onSelect: (fluxo: Fluxo) => void }) => {
  const { t } = useTranslation("chat");
  const { fluxos, loading } = useFluxos();
  const [search, setSearch] = useState("");

  const blockSummaryOf = (fluxo: Fluxo) =>
    fluxo.blocks.map((b) => t(`automations:${BLOCK_META[b.type].labelKey}`)).join(" • ");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return fluxos;
    return fluxos.filter(
      (fluxo) =>
        fluxo.name.toLowerCase().includes(term) ||
        blockSummaryOf(fluxo).toLowerCase().includes(term)
    );
  }, [fluxos, search]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <p className="text-[13px] text-muted-foreground">{t("pickers.loadingFlows")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 pb-2">
      {fluxos.length > 0 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("pickers.searchFlow")}
            className="h-11 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-[15px] text-foreground outline-none placeholder:text-muted-foreground focus:border-accent-blue"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-blue/10">
            <Workflow className="h-5 w-5 text-accent-blue" />
          </span>
          <p className="text-[14px] text-muted-foreground">
            {search.trim() ? t("pickers.noFlowsFound") : t("pickers.noFlows")}
          </p>
          {!search.trim() && (
            <p className="text-[12px] text-muted-foreground">{t("pickers.flowsHintMobile")}</p>
          )}
        </div>
      ) : (
        filtered.map((fluxo) => (
          <button
            key={fluxo.id}
            type="button"
            onClick={() => onSelect(fluxo)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-hover"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-blue/10">
              <Workflow className="h-5 w-5 text-accent-blue" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-[15px] font-medium text-foreground">{fluxo.name}</p>
                <span className="shrink-0 rounded bg-card-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {t("pickers.blockCount", { count: fluxo.blocks.length })}
                </span>
              </div>
              <p className="truncate text-[12px] text-muted-foreground">
                {blockSummaryOf(fluxo) || t("pickers.noBlocks")}
              </p>
            </div>
          </button>
        ))
      )}
    </div>
  );
};

export const MobileFluxosSheet = ({ open, onOpenChange, onSelect }: MobileFluxosSheetProps) => {
  const { t } = useTranslation("chat");

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title={t("pickers.flows")}>
      {open ? (
        <FluxosList
          onSelect={(fluxo) => {
            onOpenChange(false);
            onSelect(fluxo);
          }}
        />
      ) : null}
    </MobileSheet>
  );
};
